// Today's patient check-in state for the calendar's arrived/late indicator.
//
// Module-store singleton (same shape as threads-overview-store): started
// lazily on first subscribe, then kept fresh by a 60-second poll of
// GET /api/queue/checkins — the user-requested "refresh every 1 minute" — plus
// a debounced refetch on queueTenant WS pushes (entry_checked_in etc.), so a
// check-in from the healo_queue app lands on the calendar within seconds.
//
// The version counter bumps on EVERY poll tick (even when the roster did not
// change) — subscribed cards re-derive their "not arrived, visit in <15 min"
// red mark against the current clock each minute.

import { DateTime } from "luxon";
import { fetchQueueCheckins } from "@/lib/api/queues";
import { getAuthToken, getAuthUser } from "@/lib/api/token-store";
import { subscribeQueueEvents } from "@/lib/realtime/chat-realtime-service";

const POLL_MS = 60000;
const WS_REFRESH_DEBOUNCE_MS = 1500;

const listeners = new Set();
let started = false;
let version = 0;
let fetching = false;
/** A refresh was requested while one was in flight — replay once it settles. */
let pendingRefresh = false;
let wsRefreshTimer = null;

/** @type {Map<string, { lifecycle: string, checkedInAt: string, visitActive: boolean }>} */
let byAppointmentId = new Map();
/** Fallback for queue entries without an appointment link (manual adds). */
let byPatientId = new Map();
/** The local date the maps were fetched for ("YYYY-MM-DD"). */
let fetchedDate = "";
/**
 * Clinic (tenant) ids with a live queue instance on fetchedDate. The red
 * "not arrived" mark is derived from ABSENCE of a check-in, so it may only
 * show for clinics whose queue is actually running today — otherwise every
 * appointment near its start time would false-alarm.
 */
let fetchedClinicIds = new Set();

function emit() {
  version += 1;
  for (const listener of listeners) {
    try {
      listener(version);
    } catch {
      // ignore listener errors
    }
  }
}

async function refreshCheckins() {
  if (fetching) {
    pendingRefresh = true;
    return;
  }
  if (!String(getAuthToken() || "").trim() || !getAuthUser()?.isEmployee) {
    // Not an authenticated staff session — clear everything (including the
    // fetched-day marker, so no stale late marks derive from old data) but
    // still tick so cards re-check once auth arrives.
    byAppointmentId = new Map();
    byPatientId = new Map();
    fetchedDate = "";
    fetchedClinicIds = new Set();
    emit();
    return;
  }
  fetching = true;
  const date = DateTime.now().toISODate();
  try {
    const { items, clinics } = await fetchQueueCheckins({ date });
    const nextByAppointment = new Map();
    const nextByPatient = new Map();
    for (const item of items) {
      const row = {
        lifecycle: item.lifecycle,
        checkedInAt: item.checkedInAt,
        visitActive: item.visitActive,
      };
      if (item.appointmentId) {
        nextByAppointment.set(item.appointmentId, row);
      } else if (item.patientId) {
        nextByPatient.set(item.patientId, row);
      }
    }
    byAppointmentId = nextByAppointment;
    byPatientId = nextByPatient;
    fetchedClinicIds = new Set(clinics);
    fetchedDate = date;
  } catch {
    // fail-soft: keep the last snapshot; the next tick retries
  } finally {
    fetching = false;
    // Tick regardless of outcome — the late computation depends on "now".
    emit();
    // A WS push landed mid-flight: its change may postdate the response we
    // just applied, so replay exactly one refresh.
    if (pendingRefresh) {
      pendingRefresh = false;
      void refreshCheckins();
    }
  }
}

function scheduleWsRefresh() {
  if (wsRefreshTimer) {
    return;
  }
  wsRefreshTimer = setTimeout(() => {
    wsRefreshTimer = null;
    void refreshCheckins();
  }, WS_REFRESH_DEBOUNCE_MS);
}

function ensureStarted() {
  if (started) {
    return;
  }
  started = true;
  void refreshCheckins();
  // App-session singleton — the interval is never cleared (same convention as
  // threads-overview-store).
  setInterval(() => void refreshCheckins(), POLL_MS);
  subscribeQueueEvents(() => {
    // Any queue action (entry_checked_in, moves, unknown) is a refetch trigger.
    scheduleWsRefresh();
  });
}

/** @param {(version: number) => void} listener */
export function subscribeAppointmentCheckins(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getAppointmentCheckinVersion() {
  return version;
}

/**
 * Whether the "not arrived" (red) state may be derived for this event: the
 * store must have SUCCESSFULLY fetched the event's day and the event's clinic
 * must have a live queue instance that day. Positive (arrived) matches don't
 * need this — they come from actual data.
 * @param {{ startAt?: import("luxon").DateTime, tenantId?: string }} event
 */
export function hasCheckinCoverage(event) {
  const dayKey = event?.startAt?.toISODate?.() || "";
  if (!dayKey || dayKey !== fetchedDate) {
    return false;
  }
  const tenantId = String(event?.tenantId || "").trim();
  return Boolean(tenantId) && fetchedClinicIds.has(tenantId);
}

/**
 * The queue row for a calendar appointment event, or null. Matches by the
 * appointment's Mongo id (calendar ids carry an "a-" prefix) and falls back to
 * the patient id for manual queue adds. Only meaningful for today's events —
 * the store holds a single day.
 * @param {{ id?: string, patientId?: string, startAt?: import("luxon").DateTime }} event
 */
export function getCheckinForAppointment(event) {
  if (!event) {
    return null;
  }
  const dayKey = event.startAt?.toISODate?.() || "";
  if (!dayKey || dayKey !== fetchedDate) {
    return null;
  }
  // Calendar appointment ids are "a-<hex>" (recurring occurrences add a
  // "-YYYYMMDD" suffix); the queue stores the bare Mongo hex.
  const rawId = String(event.id || "").trim();
  const mongoId = (rawId.startsWith("a-") ? rawId.slice(2) : rawId).split("-")[0];
  if (mongoId) {
    const row = byAppointmentId.get(mongoId);
    if (row) {
      return row;
    }
  }
  const patientId = String(event.patientId || "").trim();
  if (patientId) {
    return byPatientId.get(patientId) || null;
  }
  return null;
}
